/**
 * CarrierInvoiceActionsModal
 *
 * Modal orquestador con tabs para acciones sobre una línea de factura de proveedor:
 *   Tab 0: Generar          — GenerateInternalInvoice inline (sin Dialog anidado)
 *   Tab 1: Vincular Booking — LinkBookingPanel
 *   Tab 2: Asignar Contrato — AssignContractPanel
 *   Tab 3: Conciliación     — ConciliationPanel (solo si hay booking vinculado)
 */
import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Tabs,
  Tab,
  Divider,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import {
  CloseOutlined as Close,
  ReceiptLong as InvoiceIcon,
  LinkOutlined,
  AssignmentOutlined as AssignmentIcon,
  VerifiedOutlined as ConciliationIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Euro as EuroIcon,
} from "@mui/icons-material";
import { useInternalInvoiceGeneration } from "@/hooks/use-internal-invoice-generation";
import { previewInternalInvoiceRate } from "@/services/internal-invoices-api";
import type { InternalInvoiceRatePreviewDto, RateDecision, RateMode } from "@/types/internal-invoices";
import { LinkBookingPanel } from "@/components/bookings/reconciliation/LinkBookingPanel";
import { AssignContractPanel } from "@/components/bookings/AssignContractPanel";
import { ConciliationPanel } from "./ConciliationPanel";

export interface CarrierInvoiceActionsModalProps {
  open: boolean;
  onClose: () => void;
  carrierInvoiceId: number;
  bookingId: number | null;
  linkedBookingId: number | null;
  bookingReference: string | null;
  linkedBookingReference: string | null;
  operationType: string | null;
  contractSq: string | null;
  mbl?: string | null;
  originPortId?: number | null;
  destinationPortId?: number | null;
  onActionCompleted?: () => void;
  initialTab?: number;
}

// Índices de tabs
const TAB_GENERAR = 0;
const TAB_VINCULAR = 1;
const TAB_ASIGNAR = 2;
const TAB_CONCILIACION = 3;

function getInitialTab(_linkedBookingId: number | null, _contractSq: string | null): number {
  return TAB_GENERAR;
}

// ─── Tab Generar (inline, sin Dialog anidado) ─────────────────────────────────

function formatRate(value: number | null | undefined): string {
  if (value === null || value === undefined) return "-";
  return new Intl.NumberFormat("es-ES", { maximumFractionDigits: 8 }).format(value);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return `${Number(match[3])}/${Number(match[2])}/${match[1]}`;
  }
  return new Date(value).toLocaleDateString("es-ES");
}

function RateDecisionSummary({ decision }: { decision: RateDecision }) {
  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.5, bgcolor: "background.default" }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Rate automatico calculado por sistema
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 1 }}>
        <Chip size="small" label={`Fecha factura: ${formatDate(decision.carrierInvoiceDate)}`} variant="outlined" />
        <Chip size="small" label={`Fecha rate: ${formatDate(decision.requestedRateDate)}`} color="primary" variant="outlined" />
        <Chip size="small" label={`Fecha efectiva: ${formatDate(decision.effectiveRateDate)}`} variant="outlined" />
        <Chip size="small" label={`Base: ${formatRate(decision.baseRate)}`} variant="outlined" />
        <Chip size="small" label={`Markup: ${(decision.markupPercent * 100).toFixed(2)}%`} color="warning" variant="outlined" />
        <Chip size="small" label={`Final: ${formatRate(decision.finalRate)}`} color="success" />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        {decision.policyUsed ?? "Policy"} ? {decision.rateSource ?? "BaseMstCurrencyRates"}
        {decision.isStale ? " ? usando ultimo rate disponible" : ""}
      </Typography>
    </Box>
  );
}

function GenerateTab({
  carrierInvoiceId,
  onActionCompleted,
}: {
  carrierInvoiceId: number;
  onActionCompleted?: () => void;
}) {
  const { singleGeneration, generateSingle, resetSingleState } = useInternalInvoiceGeneration();
  const { isGenerating, result } = singleGeneration;
  const [rateMode, setRateMode] = useState<RateMode>("Automatic");
  const [manualRate, setManualRate] = useState("");
  const [manualRateReason, setManualRateReason] = useState("");
  const [preview, setPreview] = useState<InternalInvoiceRatePreviewDto | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    if (rateMode !== "Automatic" || result) return;

    let cancelled = false;
    setIsPreviewing(true);
    setPreview(null);
    setPreviewError(null);

    previewInternalInvoiceRate(carrierInvoiceId)
      .then((nextPreview) => {
        if (cancelled) return;
        setPreview(nextPreview);
        if (!nextPreview.success) {
          setPreviewError(nextPreview.error?.message ?? "No hay rate automatico disponible para esta factura.");
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setPreviewError(error instanceof Error ? error.message : "No se pudo calcular el rate automatico.");
      })
      .finally(() => {
        if (!cancelled) setIsPreviewing(false);
      });

    return () => { cancelled = true; };
  }, [carrierInvoiceId, rateMode, result]);

  const manualRateNumber = Number(manualRate);
  const manualInvalid = rateMode === "Manual" && (!Number.isFinite(manualRateNumber) || manualRateNumber <= 0 || manualRateReason.trim().length === 0);
  const automaticInvalid = rateMode === "Automatic" && (!preview?.success || !preview.rateDecision);
  const canGenerate = !isGenerating && !isPreviewing && !manualInvalid && !automaticInvalid;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    resetSingleState();
    await generateSingle({
      carrierInvoiceId,
      rateMode,
      manualRate: rateMode === "Manual" ? manualRateNumber : null,
      manualRateReason: rateMode === "Manual" ? manualRateReason.trim() : null,
    });
  };

  const handleViewInvoice = (invoiceId: number) => {
    const filterParams = encodeURIComponent(JSON.stringify({ "filter-internal_invoice_id": [invoiceId] }));
    window.open(`/finanzas/facturas-internas?filters=${filterParams}`, "_blank");
    onActionCompleted?.();
  };

  if (!result) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Genera la factura interna correspondiente a esta factura de proveedor. El backend es la fuente de verdad del rate.
        </Typography>

        <RadioGroup
          row
          value={rateMode}
          onChange={(_, value) => {
            setRateMode(value as RateMode);
            resetSingleState();
          }}
        >
          <FormControlLabel value="Automatic" control={<Radio />} label="Automatico del sistema" />
          <FormControlLabel value="Manual" control={<Radio />} label="Manual auditado" />
        </RadioGroup>

        {rateMode === "Automatic" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {isPreviewing && (
              <Alert severity="info" icon={<CircularProgress size={18} />}>
                Calculando preview automatico con la fecha de factura menos un dia...
              </Alert>
            )}
            {!isPreviewing && preview?.success && preview.rateDecision && (
              <RateDecisionSummary decision={preview.rateDecision} />
            )}
            {!isPreviewing && previewError && (
              <Alert severity="warning">
                <AlertTitle>No hay rate automatico listo</AlertTitle>
                {previewError} Carga el rate maestro o usa modo manual para esta generacion individual.
              </Alert>
            )}
          </Box>
        )}

        {rateMode === "Manual" && (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "180px 1fr" }, gap: 1.5 }}>
            <TextField
              label="Rate manual final"
              type="number"
              value={manualRate}
              onChange={(event) => setManualRate(event.target.value)}
              inputProps={{ min: 0, step: "0.000001" }}
              error={manualRate.length > 0 && (!Number.isFinite(manualRateNumber) || manualRateNumber <= 0)}
              helperText="No se aplica markup."
              size="small"
            />
            <TextField
              label="Motivo de auditoria"
              value={manualRateReason}
              onChange={(event) => setManualRateReason(event.target.value)}
              error={manualRateReason.length > 0 && manualRateReason.trim().length === 0}
              helperText="Requerido para trazabilidad."
              size="small"
            />
            <Alert severity="info" sx={{ gridColumn: "1 / -1" }}>
              El rate manual se toma como rate final y no recibe el markup automatico del 1%.
            </Alert>
          </Box>
        )}

        <Box>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={!canGenerate}
            startIcon={isGenerating ? <CircularProgress size={16} color="inherit" /> : <InvoiceIcon />}
          >
            {isGenerating ? "Generando..." : "Generar Factura Interna"}
          </Button>
        </Box>
      </Box>
    );
  }

  const { success, error, internalInvoiceId, linesCreated, totalAmount, details, warnings = [] } = result;
  const rateDecision = details?.rateDecision;

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
        {success ? <SuccessIcon color="success" /> : <ErrorIcon color="error" />}
        <Typography variant="subtitle1" fontWeight={600}>
          {success ? "Factura Interna Generada" : "Error en Generacion"}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
        <Chip icon={<InvoiceIcon />} label={`Factura Proveedor: ${carrierInvoiceId}`} variant="outlined" size="small" />
        {success && internalInvoiceId && (
          <Chip icon={<InvoiceIcon />} label={`Factura Interna: ${internalInvoiceId}`} color="success" size="small" />
        )}
        <Chip icon={<EuroIcon />} label={`Total: ?${totalAmount.toFixed(2)}`} color={success ? "primary" : "default"} variant={success ? "filled" : "outlined"} size="small" />
        <Chip label={`${linesCreated} linea(s)`} color={linesCreated > 0 ? "success" : "default"} variant={linesCreated > 0 ? "filled" : "outlined"} size="small" />
      </Box>

      {success && rateDecision && <Box sx={{ mb: 2 }}><RateDecisionSummary decision={rateDecision} /></Box>}

      {success && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75, fontWeight: 600 }}>Detalle de procesamiento</Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip size="small" label={`EXTRA: ${details.extraLinesProcessed}`} color="info" variant="outlined" />
            <Chip size="small" label={`FLETE: ${details.fleteLinesProcessed}`} color="primary" variant="outlined" />
            <Chip size="small" label={`FOB: ${details.fobLinesProcessed}`} color="secondary" variant="outlined" />
            <Chip size="small" label={`Equipos: ${details.equipmentTypesProcessed}`} color="warning" variant="outlined" />
          </Box>
          {(details.linesSkippedNoMapping > 0 || details.linesSkippedNoRate > 0) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Lineas omitidas: {details.linesSkippedNoMapping} sin mapeo, {details.linesSkippedNoRate} sin tarifa
            </Typography>
          )}
        </Box>
      )}

      {!success && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      {warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Advertencias ({warnings.length})</AlertTitle>
          <List dense>
            {warnings.map((w, i) => (
              <ListItem key={i} sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 28 }}><WarningIcon color="warning" fontSize="small" /></ListItemIcon>
                <ListItemText primary={w} primaryTypographyProps={{ variant: "caption" }} />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}

      {success && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Factura generada en estado "Draft". Puede editarse antes de ser aprobada.
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
        <Button variant="outlined" size="small" onClick={() => { resetSingleState(); }} sx={{ textTransform: "none" }}>
          Generar de nuevo
        </Button>
        {success && internalInvoiceId && (
          <Button variant="contained" size="small" startIcon={<InvoiceIcon />} onClick={() => handleViewInvoice(internalInvoiceId)} sx={{ textTransform: "none" }}>
            Ver Factura Interna
          </Button>
        )}
      </Box>
    </Box>
  );
}

// ??? Modal principal ??????????????????????????????????????????????????????????

export function CarrierInvoiceActionsModal({
  open,
  onClose,
  carrierInvoiceId,
  bookingId,
  linkedBookingId: initialLinkedBookingId,
  bookingReference,
  linkedBookingReference: initialLinkedBookingRef,
  operationType,
  contractSq,
  mbl,
  originPortId,
  destinationPortId,
  onActionCompleted,
  initialTab: initialTabProp,
}: CarrierInvoiceActionsModalProps) {
  const [currentLinkedBookingId, setCurrentLinkedBookingId] = useState<number | null>(initialLinkedBookingId);
  const [currentLinkedBookingRef, setCurrentLinkedBookingRef] = useState<string | null>(initialLinkedBookingRef);
  const [actionCompleted, setActionCompleted] = useState(false);

  const initialTab = useMemo(
    () => initialTabProp ?? getInitialTab(initialLinkedBookingId, contractSq),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [activeTab, setActiveTab] = useState(initialTab);

  const hasConciliationTab = currentLinkedBookingId != null;

  const handleClose = () => {
    if (actionCompleted) onActionCompleted?.();
    onClose();
  };

  const handleLinked = (navieraBookingId: number, navieraBookingRef: string | null) => {
    setCurrentLinkedBookingId(navieraBookingId);
    setCurrentLinkedBookingRef(navieraBookingRef);
    setActionCompleted(true);
    // Ir a tab Conciliación para ver el par recién vinculado
    setActiveTab(TAB_CONCILIACION);
  };

  const handleAssignmentComplete = () => {
    setActionCompleted(true);
  };

  const handleUnlinked = () => {
    setCurrentLinkedBookingId(null);
    setCurrentLinkedBookingRef(null);
    setActionCompleted(true);
    // Ir a tab Vincular
    setActiveTab(TAB_VINCULAR);
  };

  const handleRelinked = (newNavieraId: number, newNavieraRef: string | null) => {
    setCurrentLinkedBookingId(newNavieraId);
    setCurrentLinkedBookingRef(newNavieraRef);
    setActionCompleted(true);
    setActiveTab(TAB_CONCILIACION);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, minHeight: 480 } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
        <InvoiceIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">Acciones — Factura de Proveedor</Typography>
          <Typography variant="caption" color="text.secondary">
            Booking: {bookingReference ?? `#${bookingId}`}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            label="Generar"
            icon={<InvoiceIcon fontSize="small" />}
            iconPosition="start"
            value={TAB_GENERAR}
            sx={{ textTransform: "none", minHeight: 48 }}
          />
          {!hasConciliationTab && bookingId != null && (
            <Tab
              label="Vincular Booking"
              icon={<LinkOutlined fontSize="small" />}
              iconPosition="start"
              value={TAB_VINCULAR}
              sx={{ textTransform: "none", minHeight: 48 }}
            />
          )}
          <Tab
            label="Asignar Contrato"
            icon={<AssignmentIcon fontSize="small" />}
            iconPosition="start"
            value={TAB_ASIGNAR}
            sx={{ textTransform: "none", minHeight: 48 }}
          />
          {hasConciliationTab && (
            <Tab
              label="Conciliación"
              icon={<ConciliationIcon fontSize="small" />}
              iconPosition="start"
              value={TAB_CONCILIACION}
              sx={{ textTransform: "none", minHeight: 48 }}
            />
          )}
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        {activeTab === TAB_GENERAR && (
          <GenerateTab
            carrierInvoiceId={carrierInvoiceId}
            onActionCompleted={() => { setActionCompleted(true); onActionCompleted?.(); }}
          />
        )}

        {activeTab === TAB_VINCULAR && bookingId != null && (
          <LinkBookingPanel
            bookingId={bookingId}
            bookingReference={bookingReference}
            bookingMbl={mbl}
            bookingOriginPortId={originPortId}
            bookingDestinationPortId={destinationPortId}
            onLinked={handleLinked}
          />
        )}

        {activeTab === TAB_ASIGNAR && bookingId != null && (
          <AssignContractPanel
            bookingId={bookingId}
            bookingReference={bookingReference}
            operationType={operationType}
            existingContractSq={contractSq}
            onAssignmentComplete={handleAssignmentComplete}
          />
        )}

        {activeTab === TAB_CONCILIACION && hasConciliationTab && bookingId != null && (
          <ConciliationPanel
            repoDocBookingId={bookingId}
            repoDocBookingReference={bookingReference}
            navieraBookingId={currentLinkedBookingId!}
            navieraBookingReference={currentLinkedBookingRef}
            mbl={mbl}
            originPortId={originPortId}
            destinationPortId={destinationPortId}
            onUnlinked={handleUnlinked}
            onRelinked={handleRelinked}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
